import React from "react";
import { Link } from "react-router-dom";
import { Dumbbell, Target, Compass, ArrowRight } from "lucide-react";
import { LegalPage, LegalSection, LegalList } from "../components/SiteShell";
import { usePageMeta } from "../lib/usePageMeta";

const WHO_WE_SERVE = [
  "Combat sports gyms",
  "MMA gyms",
  "Boxing gyms",
  "Muay Thai gyms",
  "CrossFit gyms",
  "Functional fitness studios",
  "Independent fitness businesses",
];

const WHAT_WE_HELP = [
  "Lead management",
  "Trial tracking",
  "Member onboarding",
  "Payment tracking",
  "Retention workflows",
  "WhatsApp communication",
  "AI-powered operational insights",
];

export default function About() {
  usePageMeta({
    title: "About Us",
    description: "Fitnudge is a WhatsApp-first operating system for fitness businesses - helping gyms manage leads, trials, memberships, renewals, and retention in one place.",
  });

  return (
    <LegalPage
      testid="about-page"
      title="About Fitnudge"
      subtitle="A WhatsApp-first operating system designed for fitness businesses."
    >
      <p>
        Most gyms today manage leads, trials, memberships, renewals, and member
        communication through WhatsApp chats, spreadsheets, and manual
        follow-ups. Fitnudge helps fitness businesses bring these workflows into
        one structured platform.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 not-prose">
        <div className="border border-border rounded-2xl p-6 bg-card" data-testid="about-mission">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
            <Target className="w-4 h-4 text-primary" strokeWidth={2.4} />
          </div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">
            Our Mission
          </p>
          <p className="text-sm text-foreground/85 leading-relaxed">
            Help fitness businesses create more consistent members through
            better systems, better follow-ups, and better retention.
          </p>
        </div>

        <div className="border border-border rounded-2xl p-6 bg-card" data-testid="about-vision">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
            <Compass className="w-4 h-4 text-primary" strokeWidth={2.4} />
          </div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-2">
            Our Vision
          </p>
          <p className="text-sm text-foreground/85 leading-relaxed">
            Build the operating system for fitness businesses - so better
            systems can power better habits and help more people stay
            consistent in their health journey.
          </p>
        </div>
      </div>

      <LegalSection title="What We Help With">
        <LegalList items={WHAT_WE_HELP} />
      </LegalSection>

      <LegalSection title="Who We Serve">
        <LegalList items={WHO_WE_SERVE} />
      </LegalSection>

      <LegalSection title="Our Belief">
        <p>
          We believe better systems create better habits. By helping fitness
          businesses operate more effectively, we help more people stay
          consistent in their health and fitness journey.
        </p>
      </LegalSection>

      <div className="flex flex-col sm:flex-row gap-3 pt-2 not-prose" data-testid="about-cta-row">
        <Link
          to="/login"
          data-testid="about-cta-get-started"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-all active:scale-[0.99] shadow-[0_12px_32px_-10px_hsl(var(--primary)/0.6)]"
        >
          <Dumbbell className="w-4 h-4" /> Get started - free
        </Link>
        <Link
          to="/contact"
          data-testid="about-cta-contact"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
        >
          Contact us <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </LegalPage>
  );
}
